// Membership status, plans, orders and web payment on the existing routes.
// Contract: platform/docs/api-inventory/membership.md and payments.md.
// Prices, durations and expiry dates always come from the server; nothing here computes an amount.
import type { Call } from './call'

/** Stored order states are pending and paid; the rest are projections the server computes on read. */
export type OrderStatus = 'pending' | 'paid' | 'expired' | 'refunded' | 'voided' | 'closed'

export interface MembershipStatus {
  active: boolean
  /** ISO time the membership runs to, or null. */
  activeUntil: string | null
  pendingDays: number
  assetUnlockBalance: number
  permanentAssetCount: number
  imageUnlocks?: { balance: number; unlockedAssetIds: string[]; lastGrantDay: string | null }
  trial?: { activatedAt: string | null }
  /** Absent for guest and anonymous viewers. */
  referral?: { code: string; uses: number; rewardedUses: number; maxRewardedUses: number }
}

export type WebPaymentChannelName = 'WECHAT_NATIVE' | 'WECHAT_H5' | 'ALIPAY_PAGE' | 'ALIPAY_WAP'
export type WebPaymentProvider = 'WECHAT' | 'ALIPAY'

export interface MembershipProduct {
  productCode: string
  name: string
  amountCents: number
  currency: string
  durationDays: number
  autoRenew: boolean
  /** Fixed end day (YYYY-MM-DD, Beijing) for the annual plan. */
  validUntil?: string
  available: boolean
}

export interface PaymentChannel {
  provider: string
  channel: string
  capability?: string
}

export interface MembershipProducts {
  products: MembershipProduct[]
  paymentEnabled: boolean
  paymentChannels: PaymentChannel[]
  /** Present only when at least one channel is configured; orders must echo it. */
  paymentAgreementVersion?: string
}

export interface PaymentOrder {
  orderId: string
  merchantOrderId?: string
  productCode: string
  provider: string
  amountCents: number
  currency?: string
  durationDays?: number
  validUntil?: string
  /** Server projection; treat unknown values as "status pending update". */
  status: OrderStatus | (string & {})
  agreementVersion?: string
  agreedAt?: string
  createdAt?: string
  expiresAt?: string
  paidAt?: string
  voidedAt?: string
  /** Paid orders: days the grant actually added. */
  membershipDays?: number
  paidAfterVoid?: boolean
  displayNote?: string
  message?: string
}

export interface PaymentRefund {
  orderId: string
  status: 'completed'
  refundedAt: string
}

/** What the browser must do to pay, after server-side validation of the provider response. */
export type WebPayment =
  | { kind: 'qr'; url: string }
  | { kind: 'redirect'; url: string }
  | { kind: 'form'; action: string; method: 'POST'; fields: Record<string, string> }

export interface MemberAssetPreview {
  assetId: string
  image: string
  previewPath: string
  title: string
  free: boolean
}

export interface CreateWebOrderInput {
  productCode: string
  channel: WebPaymentChannelName
  /** 8 to 128 chars, letters/digits/._:- ; reuse it when retrying the same attempt. */
  idempotencyKey: string
  agreementVersion: string
}

const CHANNELS: ReadonlySet<string> = new Set(['WECHAT_NATIVE', 'WECHAT_H5', 'ALIPAY_PAGE', 'ALIPAY_WAP'])

export function isWebChannel(value: unknown): value is WebPaymentChannelName {
  return typeof value === 'string' && CHANNELS.has(value)
}

export function channelProvider(channel: WebPaymentChannelName): WebPaymentProvider {
  return channel.startsWith('ALIPAY') ? 'ALIPAY' : 'WECHAT'
}

/** Web channels the server lists, in server order, without duplicates. */
export function webChannels(products: Pick<MembershipProducts, 'paymentChannels'> | null | undefined): WebPaymentChannelName[] {
  const seen: WebPaymentChannelName[] = []
  for (const item of products?.paymentChannels ?? []) {
    if (isWebChannel(item?.channel) && !seen.includes(item.channel)) seen.push(item.channel)
  }
  return seen
}

export const IDEMPOTENCY_KEY_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{7,127}$/

export function newIdempotencyKey(): string {
  const random = globalThis.crypto?.randomUUID?.()
  return `web-${random ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`}`
}

function invalid(): never {
  throw new InvalidPaymentResponse()
}

/** The provider answered with something this client will not act on. */
export class InvalidPaymentResponse extends Error {
  code = 'INVALID_PAYMENT_RESPONSE'
  constructor() {
    super('INVALID_PAYMENT_RESPONSE')
  }
}

function safeUrl(value: unknown, kind: 'native' | 'h5'): string {
  if (typeof value !== 'string' || value.length > 4096) invalid()
  let parsed: URL
  try {
    parsed = new URL(value)
  } catch {
    return invalid()
  }
  if (parsed.username || parsed.password || parsed.hash || parsed.port) invalid()
  if (kind === 'native') {
    if (parsed.protocol !== 'weixin:' || parsed.hostname !== 'wxpay' || parsed.pathname !== '/bizpayurl' || !parsed.searchParams.get('pr')) invalid()
  } else if (parsed.protocol !== 'https:' || parsed.hostname !== 'wx.tenpay.com' || !parsed.pathname.startsWith('/cgi-bin/mmpayweb-bin/')) {
    invalid()
  }
  return parsed.href
}

/**
 * Validates the `payment` object of POST /api/payments/web/start for the channel that was requested.
 * WECHAT_NATIVE gives a weixin:// code URL (draw a QR), WECHAT_H5 an https redirect, Alipay a signed auto-post form.
 */
export function parseWebPayment(channel: WebPaymentChannelName, payment: unknown): WebPayment {
  if (!payment || typeof payment !== 'object' || Array.isArray(payment)) invalid()
  const body = payment as Record<string, unknown>
  if (channel === 'WECHAT_NATIVE') return { kind: 'qr', url: safeUrl(body.codeUrl, 'native') }
  if (channel === 'WECHAT_H5') return { kind: 'redirect', url: safeUrl(body.h5Url, 'h5') }
  const fieldsIn = body.fields
  if (body.method !== 'POST' || body.action !== 'https://openapi.alipay.com/gateway.do' || !fieldsIn || typeof fieldsIn !== 'object' || Array.isArray(fieldsIn)) invalid()
  const fields: Record<string, string> = {}
  for (const [key, value] of Object.entries(fieldsIn as Record<string, unknown>)) {
    if (!/^[A-Za-z0-9_]+$/.test(key) || typeof value !== 'string' || value.length > 8192) invalid()
    fields[key] = value as string
  }
  return { kind: 'form', action: 'https://openapi.alipay.com/gateway.do', method: 'POST', fields }
}

export function createMembershipApi(call: Call) {
  return {
    /** Public. 200 inactive for guests and anonymous viewers; 403 FORMAL_ACCOUNT_REQUIRED for other non-account sessions. */
    status: () => call<MembershipStatus>({ method: 'GET', path: '/api/membership/status' }),
    /** Public catalogue with the current agreement version and the enabled web payment channels. */
    products: () => call<MembershipProducts>({ method: 'GET', path: '/api/membership/products' }),
    /** Paid and refunded orders only, newest first. */
    orders: () => call<{ orders: PaymentOrder[] }>({ method: 'GET', path: '/api/membership/orders' }),
    /** One order of the caller in any state. May settle a WeChat order on the server before answering. */
    order: (orderId: string) =>
      call<{ order: PaymentOrder; message?: string }>({ method: 'GET', path: `/api/payments/orders/${encodeURIComponent(orderId)}` }),
    refunds: () => call<{ refunds: PaymentRefund[] }>({ method: 'GET', path: '/api/membership/refunds' }),

    /**
     * Step one of a web purchase. The provider is derived from the channel as the legacy site does.
     * No amount is sent: the server prices the product. 201 new order, 200 replay of the same key.
     */
    createWebOrder: (input: CreateWebOrderInput) =>
      call<{ order: PaymentOrder }>({
        method: 'POST',
        path: '/api/payments/orders',
        body: {
          productCode: input.productCode,
          provider: channelProvider(input.channel),
          idempotencyKey: input.idempotencyKey,
          agreementAccepted: true,
          agreementVersion: input.agreementVersion,
        },
      }),
    /** Step two: ask the provider for a QR code URL, redirect URL or Alipay form. */
    startWebPayment: async (orderId: string, channel: WebPaymentChannelName) => {
      const result = await call<{ order?: PaymentOrder; payment: unknown }>({
        method: 'POST',
        path: '/api/payments/web/start',
        body: { orderId, channel },
      })
      return { order: result.order, payment: parseWebPayment(channel, result.payment) }
    },

    /** Preview manifest for the member image wall (public; 404 when not served). Image bytes are not fetched here. */
    assetPreviews: () => call<{ previews: MemberAssetPreview[] }>({ method: 'GET', path: '/api/membership/asset-previews' }),
  }
}

export type MembershipApi = ReturnType<typeof createMembershipApi>
